import { useEffect, useRef, useState, useCallback } from 'react';
import { adminSupabase } from '@/integrations/supabase/adminClient';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  MessageSquare, X, Plus, Send, ChevronLeft, Minus, Smile,
  Users, UserPlus, Hash, Check, Search,
} from 'lucide-react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { format, isToday, isYesterday } from 'date-fns';

/* ─── Tick component ─────────────────────────────────────── */
// sending = 1 grey tick (not yet saved)
// sent    = 2 grey ticks (saved in DB, recipient not yet online)
// delivered = 2 grey ticks darker (recipient logged in / app loaded)
// seen    = 2 blue ticks (recipient opened the chat thread)
type TickStatus = 'sending' | 'sent' | 'delivered' | 'seen';

function MessageTicks({ status }: { status: TickStatus }) {
  if (status === 'sending') {
    return (
      <span className="inline-flex items-center ml-1">
        <Check className="h-3 w-3 opacity-40" />
      </span>
    );
  }
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center ml-1" style={{ width: '16px', position: 'relative', display: 'inline-flex' }}>
        <Check className="h-3 w-3 absolute" style={{ left: 0, opacity: 0.4 }} />
        <Check className="h-3 w-3 absolute" style={{ left: '6px', opacity: 0.4 }} />
        <span style={{ width: '16px', display: 'inline-block' }} />
      </span>
    );
  }
  if (status === 'delivered') {
    return (
      <span className="inline-flex items-center ml-1" style={{ width: '16px', position: 'relative', display: 'inline-flex' }}>
        <Check className="h-3 w-3 absolute" style={{ left: 0, opacity: 0.7 }} />
        <Check className="h-3 w-3 absolute" style={{ left: '6px', opacity: 0.7 }} />
        <span style={{ width: '16px', display: 'inline-block' }} />
      </span>
    );
  }
  // seen — double ticks, blue
  return (
    <span className="inline-flex items-center ml-1" style={{ width: '16px', position: 'relative', display: 'inline-flex' }}>
      <Check className="h-3 w-3 absolute" style={{ left: 0, color: '#60a5fa' }} />
      <Check className="h-3 w-3 absolute" style={{ left: '6px', color: '#60a5fa' }} />
      <span style={{ width: '16px', display: 'inline-block' }} />
    </span>
  );
}

/* ─── Types ─────────────────────────────────────────────── */
interface AdminUser {
  id: string; userId: string; email: string; fullName: string | null;
  avatarUrl?: string | null; avatarColor?: string | null;
  jobTitle?: string | null; department?: string | null;
}
interface AdminMessage {
  id: string; conversation_id: string; sender_user_id: string;
  body: string; read_by: string[]; delivered_by: string[]; created_at: string;
}
interface AdminConversation {
  id: string; participant_ids: string[];
  last_message_at: string | null; last_message_preview: string | null;
  created_at: string; name?: string | null; is_group?: boolean;
  otherUser?: AdminUser;
  unreadCount?: number;
}

/* ─── Helpers ────────────────────────────────────────────── */
const AVATAR_COLORS = [
  { key: 'blue', bg: 'bg-blue-500' }, { key: 'emerald', bg: 'bg-emerald-500' },
  { key: 'violet', bg: 'bg-violet-500' }, { key: 'amber', bg: 'bg-amber-500' },
  { key: 'rose', bg: 'bg-rose-500' }, { key: 'cyan', bg: 'bg-cyan-500' },
];
const getInitials = (name?: string | null, email?: string) =>
  name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : (email?.[0] || '?').toUpperCase();
const getAvatarBg = (color?: string | null) =>
  AVATAR_COLORS.find(c => c.key === color)?.bg ?? 'bg-primary';

const formatMsgTime = (iso: string) => {
  const d = new Date(iso);
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return `Yesterday ${format(d, 'HH:mm')}`;
  return format(d, 'MMM d, HH:mm');
};

/* ─── Draggable hook ────────────────────────────────────── */
function useDraggable(initialPos: { x: number; y: number }) {
  const [pos, setPos] = useState(initialPos);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    dragging.current = true;
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }, [pos]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const newX = e.clientX - offset.current.x;
      const newY = e.clientY - offset.current.y;
      const maxX = window.innerWidth - 340;
      const maxY = window.innerHeight - 60;
      setPos({ x: Math.max(0, Math.min(newX, maxX)), y: Math.max(0, Math.min(newY, maxY)) });
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  return { pos, onMouseDown };
}

/* ─── New Chat / Group dialog ───────────────────────────── */
type NewChatMode = 'dm' | 'group';

interface NewChatDialogProps {
  open: boolean;
  onClose: () => void;
  allAdmins: AdminUser[];
  currentUserId: string;
  onStartDM: (peerId: string) => void;
  onCreateGroup: (name: string, memberIds: string[]) => void;
}

function NewChatDialog({ open, onClose, allAdmins, currentUserId, onStartDM, onCreateGroup }: NewChatDialogProps) {
  const [mode, setMode] = useState<NewChatMode>('dm');
  const [search, setSearch] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const filtered = allAdmins.filter(a =>
    a.userId !== currentUserId &&
    (a.fullName?.toLowerCase().includes(search.toLowerCase()) ||
      a.email.toLowerCase().includes(search.toLowerCase()))
  );

  const reset = () => { setMode('dm'); setSearch(''); setGroupName(''); setSelected([]); };
  const handleClose = () => { reset(); onClose(); };

  const toggleMember = (id: string) =>
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleCreateGroup = () => {
    if (!groupName.trim() || selected.length < 1) return;
    onCreateGroup(groupName.trim(), selected);
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && handleClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{mode === 'dm' ? 'New Message' : 'New Group'}</DialogTitle>
        </DialogHeader>

        <div className="flex rounded-lg border border-border overflow-hidden">
          <button
            className={cn('flex-1 py-1.5 text-sm flex items-center justify-center gap-1.5 transition-colors',
              mode === 'dm' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted')}
            onClick={() => setMode('dm')}
          >
            <MessageSquare className="h-3.5 w-3.5" /> Direct
          </button>
          <button
            className={cn('flex-1 py-1.5 text-sm flex items-center justify-center gap-1.5 transition-colors',
              mode === 'group' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted')}
            onClick={() => setMode('group')}
          >
            <Users className="h-3.5 w-3.5" /> Group
          </button>
        </div>

        {mode === 'group' && (
          <Input
            placeholder="Group name…"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            autoFocus
          />
        )}

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={mode === 'dm' ? 'Search team member…' : 'Add members…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8"
            autoFocus={mode === 'dm'}
          />
        </div>

        <ScrollArea className="h-52">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">No team members found</p>
          ) : filtered.map(a => {
            const isSelected = selected.includes(a.userId);
            return (
              <button
                key={a.userId}
                onClick={() => mode === 'dm' ? (onStartDM(a.userId), handleClose()) : toggleMember(a.userId)}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted text-left transition-colors"
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={a.avatarUrl || undefined} />
                  <AvatarFallback className={cn('text-xs text-white', getAvatarBg(a.avatarColor))}>
                    {getInitials(a.fullName, a.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.fullName || a.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.jobTitle || a.department || a.email}</p>
                </div>
                {mode === 'group' && (
                  <div className={cn(
                    'h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                    isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/40'
                  )}>
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                )}
              </button>
            );
          })}
        </ScrollArea>

        {mode === 'group' && (
          <Button
            className="w-full"
            disabled={!groupName.trim() || selected.length < 1}
            onClick={handleCreateGroup}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Create Group ({selected.length} member{selected.length !== 1 ? 's' : ''})
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main component ─────────────────────────────────────── */
export function AdminChatFloating() {
  const { adminUser } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [conversations, setConversations] = useState<AdminConversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [msgText, setMsgText] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [allAdmins, setAllAdmins] = useState<AdminUser[]>([]);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const activeConvIdRef = useRef<string | null>(null);
  // Track the last fetched message timestamp to know if there are new ones
  const lastMessageCountRef = useRef<number>(0);

  const { pos, onMouseDown } = useDraggable({
    x: window.innerWidth - 360,
    y: window.innerHeight - 520,
  });

  // Keep ref in sync so polling callbacks always use latest value
  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);

  const adminMap = Object.fromEntries(allAdmins.map(a => [a.userId, a]));

  /* ── Load all admin users ─────────────────────────────── */
  useEffect(() => {
    if (!adminUser) return;
    adminSupabase
      .from('admin_users')
      .select('id, user_id, email, full_name, avatar_url, avatar_color, job_title, department')
      .then(({ data, error }) => {
        if (error) { console.error('[Chat] Failed to load admins:', error.message); return; }
        setAllAdmins((data || []).map(d => ({
          id: d.id, userId: d.user_id, email: d.email, fullName: d.full_name,
          avatarUrl: d.avatar_url, avatarColor: d.avatar_color,
          jobTitle: d.job_title, department: d.department,
        })));
      });
  }, [adminUser?.userId]);

  /* ── Load conversations ───────────────────────────────── */
  const fetchConvs = useCallback(async () => {
    if (!adminUser) return;
    let data: any[] | null = null;
    try {
      const res = await adminSupabase
        .from('admin_conversations')
        .select('*')
        .contains('participant_ids', [adminUser.userId])
        .order('last_message_at', { ascending: false, nullsFirst: false });
      if (res.error) { console.error('[Chat] fetchConvs error:', res.error.message); return; }
      data = res.data;
    } catch (e: any) {
      if (e?.message !== 'Failed to fetch') console.error('[Chat] fetchConvs error:', e?.message || e);
      return;
    }

    // Build fresh admin map inline (avoid stale closure)
    const { data: adminData } = await adminSupabase
      .from('admin_users')
      .select('id, user_id, email, full_name, avatar_url, avatar_color, job_title, department');
    const freshAdmins: AdminUser[] = (adminData || []).map(d => ({
      id: d.id, userId: d.user_id, email: d.email, fullName: d.full_name,
      avatarUrl: d.avatar_url, avatarColor: d.avatar_color,
      jobTitle: d.job_title, department: d.department,
    }));
    const freshMap = Object.fromEntries(freshAdmins.map(a => [a.userId, a]));
    if (freshAdmins.length > 0) setAllAdmins(freshAdmins);

    // Fetch unread counts per conversation
    const convIds = (data || []).map((c: any) => c.id);
    const unreadMap: Record<string, number> = {};
    if (convIds.length > 0) {
      // Get all unread messages across all conversations in one query
      const { data: unreadMsgs } = await adminSupabase
        .from('admin_messages')
        .select('conversation_id')
        .in('conversation_id', convIds)
        .neq('sender_user_id', adminUser.userId)
        .not('read_by', 'cs', `{"${adminUser.userId}"}`);
      
      for (const msg of (unreadMsgs || [])) {
        unreadMap[msg.conversation_id] = (unreadMap[msg.conversation_id] || 0) + 1;
      }
    }

    const enriched = (data || []).map((c: any) => {
      const conv: AdminConversation = { ...c, unreadCount: unreadMap[c.id] || 0 };
      if (c.is_group) return conv;
      const otherId = (c.participant_ids as string[]).find(id => id !== adminUser.userId);
      return { ...conv, otherUser: otherId ? freshMap[otherId] : undefined };
    });

    // Sort: unread conversations first, then by last_message_at
    enriched.sort((a: AdminConversation, b: AdminConversation) => {
      const aUnread = (a.unreadCount || 0) > 0 ? 1 : 0;
      const bUnread = (b.unreadCount || 0) > 0 ? 1 : 0;
      if (aUnread !== bUnread) return bUnread - aUnread;
      const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bTime - aTime;
    });

    setConversations(enriched);
  }, [adminUser?.userId]);

  useEffect(() => {
    if (!adminUser) return;
    fetchConvs();
    // Poll conversations every 10s — realtime on conversations is supplementary
    const poll = setInterval(fetchConvs, 10_000);
    return () => clearInterval(poll);
  }, [fetchConvs, adminUser?.userId]);

  // Mark ALL messages across all conversations as delivered when user loads app
  useEffect(() => {
    if (!adminUser || conversations.length === 0) return;
    conversations.forEach(c => markAsDelivered(c.id));
  }, [adminUser?.userId, conversations.length]);

  /* ── Mark messages as delivered (recipient is online/loaded) ── */
  const markAsDelivered = useCallback(async (convId: string) => {
    if (!adminUser) return;
    // Mark messages sent by others that haven't been delivered to me yet
    const { data: undelivered } = await adminSupabase
      .from('admin_messages')
      .select('id, delivered_by')
      .eq('conversation_id', convId)
      .neq('sender_user_id', adminUser.userId)
      .not('delivered_by', 'cs', `{"${adminUser.userId}"}`);

    if (undelivered && undelivered.length > 0) {
      await Promise.all(
        undelivered.map((msg: any) =>
          adminSupabase
            .from('admin_messages')
            .update({ delivered_by: [...(msg.delivered_by || []), adminUser.userId] })
            .eq('id', msg.id)
        )
      );
    }
  }, [adminUser?.userId]);

  /* ── Mark messages as read ───────────────────────────── */
  const markAsRead = useCallback(async (convId: string) => {
    if (!adminUser) return;
    // Fetch messages not yet read by current user (also updates delivered)
    const { data: unread } = await adminSupabase
      .from('admin_messages')
      .select('id, read_by, delivered_by')
      .eq('conversation_id', convId)
      .not('read_by', 'cs', `{"${adminUser.userId}"}`);

    if (unread && unread.length > 0) {
      await Promise.all(
        unread.map((msg: any) => {
          const newDelivered = msg.delivered_by?.includes(adminUser.userId)
            ? msg.delivered_by
            : [...(msg.delivered_by || []), adminUser.userId];
          return adminSupabase
            .from('admin_messages')
            .update({ read_by: [...(msg.read_by || []), adminUser.userId], delivered_by: newDelivered })
            .eq('id', msg.id);
        })
      );
    }
  }, [adminUser?.userId]);

  /* ── Load messages for active conversation ────────────── */
  const fetchMessages = useCallback(async (convId: string, replace = false) => {
    const { data, error } = await adminSupabase
      .from('admin_messages')
      .select('*')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: true });

    if (error) { console.error('[Chat] fetchMessages error:', error.message); return; }
    const rows = (data || []) as AdminMessage[];

    setMessages(prev => {
      if (replace) return rows;
      // Merge: keep optimistic messages not yet confirmed
      const confirmedBodies = new Set(rows.map(m => `${m.body}|${m.sender_user_id}`));
      const pendingOptimistic = prev.filter(
        m => m.id.startsWith('optimistic-') && !confirmedBodies.has(`${m.body}|${m.sender_user_id}`)
      );
      const merged = [...rows, ...pendingOptimistic];
      merged.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      return merged;
    });

    lastMessageCountRef.current = rows.length;
  }, []);

  useEffect(() => {
    if (!activeConvId) { setMessages([]); return; }

    // Initial load + mark as delivered (we're online) + mark as read when viewing
    fetchMessages(activeConvId, true);
    markAsDelivered(activeConvId);
    markAsRead(activeConvId);

    // Poll every 3 seconds for new messages — this is the RELIABLE path
    const poll = setInterval(() => {
      if (activeConvIdRef.current === activeConvId) {
        fetchMessages(activeConvId, false);
        markAsRead(activeConvId);
      }
    }, 3_000);

    // Realtime as enhancement (may not work with isolated auth but we try)
    let channel: ReturnType<typeof adminSupabase.channel> | null = null;
    adminSupabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      channel = adminSupabase
        .channel(`admin_chat_${activeConvId}`, {
          config: { broadcast: { self: true } },
        })
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'admin_messages',
            filter: `conversation_id=eq.${activeConvId}`,
          },
          () => {
            if (activeConvIdRef.current === activeConvId) {
              fetchMessages(activeConvId, false);
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('[Chat] Realtime subscribed for conv', activeConvId);
          }
        });
    });

    return () => {
      clearInterval(poll);
      if (channel) adminSupabase.removeChannel(channel);
    };
  }, [activeConvId, fetchMessages, markAsDelivered, markAsRead]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  /* ── Send message ─────────────────────────────────────── */
  const sendMessage = async () => {
    if (!msgText.trim() || !activeConvId || !adminUser || sending) return;
    const body = msgText.trim();
    setMsgText('');
    setSending(true);

    // Optimistic insert
    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticMsg: AdminMessage = {
      id: optimisticId,
      conversation_id: activeConvId,
      sender_user_id: adminUser.userId,
      body,
      read_by: [adminUser.userId],
      delivered_by: [adminUser.userId],
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimisticMsg]);

    const { data, error } = await adminSupabase
      .from('admin_messages')
      .insert({
        conversation_id: activeConvId,
        sender_user_id: adminUser.userId,
        body,
      })
      .select()
      .maybeSingle();

    setSending(false);

    if (error) {
      console.error('[Chat] Send error:', error.message);
      // Remove optimistic on failure
      setMessages(prev => prev.filter(m => m.id !== optimisticId));
      return;
    }

    if (data) {
      // Replace optimistic with confirmed message
      setMessages(prev => prev.map(m => m.id === optimisticId ? (data as AdminMessage) : m));
    }

    // Update conversation preview immediately
    setConversations(prev => prev.map(c =>
      c.id === activeConvId
        ? { ...c, last_message_at: new Date().toISOString(), last_message_preview: body.slice(0, 100) }
        : c
    ));

    // Re-fetch to stay in sync (chat notifications live on the chat FAB badge, not the bell)
    fetchMessages(activeConvId, false);
  };

  /* ── Start DM ─────────────────────────────────────────── */
  const startDM = async (peerId: string) => {
    if (!adminUser) return;
    // Check for existing 1:1 conversation
    const existing = conversations.find(c =>
      !c.is_group &&
      c.participant_ids.length === 2 &&
      c.participant_ids.includes(peerId) &&
      c.participant_ids.includes(adminUser.userId)
    );
    if (existing) { setActiveConvId(existing.id); return; }

    const { data, error } = await adminSupabase
      .from('admin_conversations')
      .insert({
        participant_ids: [adminUser.userId, peerId],
        created_by: adminUser.userId,
        is_group: false,
      })
      .select()
      .single();

    if (error) { console.error('[Chat] startDM error:', error.message); return; }
    if (data) {
      const otherUser = allAdmins.find(a => a.userId === peerId);
      setConversations(prev => [{ ...data, otherUser }, ...prev]);
      setActiveConvId(data.id);
    }
  };

  /* ── Create group ─────────────────────────────────────── */
  const createGroup = async (name: string, memberIds: string[]) => {
    if (!adminUser) return;
    const allIds = [adminUser.userId, ...memberIds];
    const { data, error } = await adminSupabase
      .from('admin_conversations')
      .insert({
        participant_ids: allIds,
        created_by: adminUser.userId,
        is_group: true,
        name,
      })
      .select()
      .single();

    if (error) { console.error('[Chat] createGroup error:', error.message); return; }
    if (data) {
      setConversations(prev => [{ ...data }, ...prev]);
      setActiveConvId(data.id);
    }
  };

  /* ── Global unread count across ALL conversations ───────── */
  const [globalUnread, setGlobalUnread] = useState(0);

  const fetchGlobalUnread = useCallback(async () => {
    if (!adminUser) return;
    // Get all conversations this user is part of
    const { data: convs } = await adminSupabase
      .from('admin_conversations')
      .select('id')
      .contains('participant_ids', [adminUser.userId]);
    if (!convs?.length) { setGlobalUnread(0); return; }

    const convIds = convs.map((c: any) => c.id);
    // Count messages from others not yet read by me
    const { count } = await adminSupabase
      .from('admin_messages')
      .select('id', { count: 'exact', head: true })
      .in('conversation_id', convIds)
      .neq('sender_user_id', adminUser.userId)
      .not('read_by', 'cs', `{"${adminUser.userId}"}`);

    setGlobalUnread(count ?? 0);
  }, [adminUser?.userId]);

  // Poll unread count every 5s regardless of chat open state
  useEffect(() => {
    if (!adminUser) return;
    fetchGlobalUnread();
    const poll = setInterval(fetchGlobalUnread, 5_000);

    // Also subscribe to new messages via realtime for instant badge updates
    let channel: ReturnType<typeof adminSupabase.channel> | null = null;
    adminSupabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      channel = adminSupabase
        .channel('global_unread_watch')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'admin_messages' }, () => {
          fetchGlobalUnread();
        })
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'admin_messages' }, () => {
          fetchGlobalUnread();
        })
        .subscribe();
    });

    return () => {
      clearInterval(poll);
      if (channel) adminSupabase.removeChannel(channel);
    };
  }, [adminUser?.userId, fetchGlobalUnread]);

  // Clear badge for active conversation when opened
  useEffect(() => {
    if (activeConvId && open) fetchGlobalUnread();
  }, [activeConvId, open, fetchGlobalUnread]);
  /* ── Derived state ────────────────────────────────────── */
  const activeConv = conversations.find(c => c.id === activeConvId);

  const convTitle = activeConv
    ? (activeConv.is_group
        ? (activeConv.name || 'Group')
        : (activeConv.otherUser?.fullName || activeConv.otherUser?.email || 'Chat'))
    : 'Team Messages';

  const groupSubtitle = activeConv?.is_group
    ? activeConv.participant_ids
        .filter(id => id !== adminUser?.userId)
        .map(id => adminMap[id]?.fullName || adminMap[id]?.email || '?')
        .slice(0, 3).join(', ')
    : '';

  if (!adminUser) return null;

  return (
    <>
      <NewChatDialog
        open={newChatOpen}
        onClose={() => setNewChatOpen(false)}
        allAdmins={allAdmins}
        currentUserId={adminUser.userId}
        onStartDM={startDM}
        onCreateGroup={createGroup}
      />

      {/* Floating trigger button — always bottom-right */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => { setOpen(o => !o); setMinimized(false); }}
          className="relative w-12 h-12 rounded-full bg-[hsl(var(--primary))] text-primary-foreground shadow-xl hover:opacity-90 transition-all flex items-center justify-center"
          aria-label="Team chat"
        >
          <MessageSquare className="w-5 h-5" />
          {!open && globalUnread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-black flex items-center justify-center px-1 ring-2 ring-background shadow-lg animate-in zoom-in-75 duration-200">
              {globalUnread > 9 ? '9+' : globalUnread}
            </span>
          )}
        </button>

        {/* Minimised pill */}
        {open && minimized && (
          <div
            className="bg-primary text-primary-foreground rounded-full px-4 py-2 flex items-center gap-2 cursor-pointer shadow-lg hover:opacity-90 transition-opacity select-none"
            onClick={() => setMinimized(false)}
          >
            <MessageSquare className="w-4 h-4" />
            <span className="text-sm font-medium">Team Messages</span>
          </div>
        )}

        {/* Chat panel */}
        {open && !minimized && (
            <div className="w-[340px] h-[480px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden">
              {/* Header */}
              <div
                className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between shrink-0 cursor-grab active:cursor-grabbing select-none"
                onMouseDown={onMouseDown}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {activeConvId && (
                    <button
                      onClick={() => setActiveConvId(null)}
                      className="opacity-80 hover:opacity-100 shrink-0 p-0.5"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  )}
                  {activeConv?.is_group && <Hash className="h-3.5 w-3.5 shrink-0 opacity-80" />}
                  <div className="min-w-0">
                    <span className="font-semibold text-sm truncate block">{convTitle}</span>
                    {activeConv?.is_group && groupSubtitle && (
                      <span className="text-[10px] opacity-70 truncate block">{groupSubtitle}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!activeConvId && (
                    <button
                      onClick={() => setNewChatOpen(true)}
                      className="opacity-80 hover:opacity-100 p-1.5 rounded hover:bg-primary-foreground/10 transition-colors"
                      title="New message or group"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setMinimized(true)}
                    className="opacity-80 hover:opacity-100 p-1.5 rounded hover:bg-primary-foreground/10 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { setOpen(false); setActiveConvId(null); }}
                    className="opacity-80 hover:opacity-100 p-1.5 rounded hover:bg-primary-foreground/10 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Body — conversation list OR message thread */}
              {!activeConvId ? (
                /* ── Conversation list ── */
                <ScrollArea className="flex-1">
                  {conversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full py-12 text-center px-4">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                        <MessageSquare className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-sm font-medium">No conversations yet</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Click <Plus className="inline h-3 w-3" /> to message a teammate or create a group.
                      </p>
                    </div>
                  ) : (
                    <div>
                      {conversations.map(c => {
                        const hasUnread = (c.unreadCount || 0) > 0;
                        return (
                        <button
                          key={c.id}
                          onClick={() => setActiveConvId(c.id)}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/30 text-left",
                            hasUnread && "bg-primary/5"
                          )}
                        >
                          <div className="relative shrink-0">
                            {c.is_group ? (
                              <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center">
                                <Users className="h-4 w-4 text-primary" />
                              </div>
                            ) : (
                              <Avatar className="h-9 w-9">
                                <AvatarImage src={c.otherUser?.avatarUrl || undefined} />
                                <AvatarFallback className={cn('text-xs text-white', getAvatarBg(c.otherUser?.avatarColor))}>
                                  {getInitials(c.otherUser?.fullName, c.otherUser?.email)}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn("text-sm truncate", hasUnread ? "font-bold" : "font-medium")}>
                              {c.is_group ? (c.name || 'Group') : (c.otherUser?.fullName || c.otherUser?.email || 'Unknown')}
                            </p>
                            <p className={cn("text-xs truncate", hasUnread ? "text-foreground font-medium" : "text-muted-foreground")}>
                              {c.last_message_preview || (c.is_group ? `${c.participant_ids.length} members` : 'No messages yet')}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {c.last_message_at && (
                              <span className={cn("text-[10px]", hasUnread ? "text-primary font-semibold" : "text-muted-foreground")}>
                                {format(new Date(c.last_message_at), 'HH:mm')}
                              </span>
                            )}
                            {hasUnread && (
                              <span className="min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center px-1">
                                {c.unreadCount! > 9 ? '9+' : c.unreadCount}
                              </span>
                            )}
                          </div>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              ) : (
                /* ── Message thread ── */
                <>
                  <ScrollArea className="flex-1 px-3 pt-3">
                    <div className="space-y-1 pb-2">
                      {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center">
                          <p className="text-xs text-muted-foreground">No messages yet. Say hello! 👋</p>
                        </div>
                      ) : messages.map((m, idx) => {
                        const isMine = m.sender_user_id === adminUser.userId;
                        const isOptimistic = m.id.startsWith('optimistic-');
                        const sender = adminMap[m.sender_user_id];
                        // Show date separator
                        const prevMsg = idx > 0 ? messages[idx - 1] : null;
                        const showDateSep = !prevMsg ||
                          format(new Date(m.created_at), 'yyyy-MM-dd') !== format(new Date(prevMsg.created_at), 'yyyy-MM-dd');

                        return (
                          <div key={m.id}>
                            {showDateSep && (
                              <div className="flex items-center gap-2 my-3">
                                <div className="flex-1 h-px bg-border/50" />
                                <span className="text-[10px] text-muted-foreground px-2">
                                  {isToday(new Date(m.created_at)) ? 'Today' :
                                    isYesterday(new Date(m.created_at)) ? 'Yesterday' :
                                    format(new Date(m.created_at), 'MMM d, yyyy')}
                                </span>
                                <div className="flex-1 h-px bg-border/50" />
                              </div>
                            )}
                            <div className={cn('flex gap-2 mb-1', isMine && 'flex-row-reverse')}>
                              {!isMine && (
                                <Avatar className="h-6 w-6 shrink-0 mt-0.5">
                                  <AvatarImage src={sender?.avatarUrl || undefined} />
                                  <AvatarFallback className={cn('text-[10px] text-white', getAvatarBg(sender?.avatarColor))}>
                                    {getInitials(sender?.fullName, sender?.email)}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                              <div className={cn(
                                'max-w-[78%] rounded-2xl px-3 py-2 text-sm transition-opacity',
                                isMine
                                  ? 'bg-primary text-primary-foreground rounded-tr-sm'
                                  : 'bg-muted rounded-tl-sm',
                                isOptimistic && 'opacity-60',
                              )}>
                                {activeConv?.is_group && !isMine && (
                                  <p className="text-[10px] font-semibold opacity-70 mb-0.5">
                                    {sender?.fullName || sender?.email || 'Unknown'}
                                  </p>
                                )}
                                <p className="leading-snug break-words">{m.body}</p>
                                <div className={cn(
                                  'flex items-center gap-0.5 mt-0.5',
                                  isMine ? 'justify-end' : 'justify-start',
                                )}>
                                  <span className={cn(
                                    'text-[10px]',
                                    isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'
                                  )}>
                                    {formatMsgTime(m.created_at)}
                                  </span>
                                  {isMine && (() => {
                                    if (isOptimistic) return <MessageTicks status="sending" />;
                                    // Seen = read_by contains someone other than the sender
                                    const otherParticipants = (activeConv?.participant_ids || []).filter(
                                      id => id !== adminUser.userId
                                    );
                                    const seenByOther = otherParticipants.some(id => m.read_by?.includes(id));
                                    const deliveredToOther = otherParticipants.some(id => m.delivered_by?.includes(id));
                                    const tickStatus: TickStatus = seenByOther ? 'seen' : deliveredToOther ? 'delivered' : 'sent';
                                    return <MessageTicks status={tickStatus} />;
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={bottomRef} />
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <div className="p-3 border-t border-border flex gap-2 shrink-0 relative bg-card">
                    {showEmoji && (
                      <div className="absolute bottom-full right-0 mb-1 z-50 shadow-xl rounded-xl overflow-hidden">
                        <EmojiPicker
                          theme={Theme.DARK}
                          onEmojiClick={(e: EmojiClickData) => {
                            const input = inputRef.current;
                            if (input) {
                              const start = input.selectionStart ?? msgText.length;
                              const end = input.selectionEnd ?? msgText.length;
                              const newVal = msgText.slice(0, start) + e.emoji + msgText.slice(end);
                              setMsgText(newVal);
                              setTimeout(() => {
                                input.focus();
                                input.setSelectionRange(start + e.emoji.length, start + e.emoji.length);
                              }, 0);
                            } else {
                              setMsgText(prev => prev + e.emoji);
                            }
                            setShowEmoji(false);
                          }}
                          height={350}
                          width={300}
                        />
                      </div>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowEmoji(v => !v)}
                      type="button"
                    >
                      <Smile className="h-4 w-4" />
                    </Button>
                    <Input
                      ref={inputRef}
                      placeholder="Type a message…"
                      value={msgText}
                      onChange={e => setMsgText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                          setShowEmoji(false);
                        }
                      }}
                      className="flex-1 h-8 text-sm"
                      autoFocus
                    />
                    <Button
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => { sendMessage(); setShowEmoji(false); }}
                      disabled={!msgText.trim() || sending}
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
    </>
  );
}
